import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import {
  Layers, RefreshCw, CheckCircle2, ClipboardList, Building2,
  User, Calendar, Eye, Edit3, RotateCcw, Loader2,
} from "lucide-react";
import FormateoTablaModal from "./FormateoTablaModal";

function StatPill({ count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const color = total === 0 ? "text-gray-400" : count === total ? "text-emerald-600" : count === 0 ? "text-gray-400" : "text-amber-600";
  return (
    <span className={`text-xs font-semibold ${color}`} data-testid="card-frente-progress">
      {count}/{total} ítems con frente {total > 0 && <span className="text-gray-400 font-normal">({pct}%)</span>}
    </span>
  );
}

function BudgetCard({ b, formatted, onOpen, onReopen, reopening }) {
  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-5"
      data-testid={`formateo-card-${b._id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-bold text-gray-900 truncate">{b.nombre}</h3>
          <p className="text-xs text-gray-500 font-mono mt-0.5">{b.number} · REV{String(b.revision).padStart(2, "0")}</p>
        </div>
        {formatted && (
          <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-lg flex-shrink-0">
            <CheckCircle2 className="w-3.5 h-3.5" /> Formateado
          </span>
        )}
      </div>

      <div className="mt-3 space-y-1.5 text-sm text-gray-600">
        <p className="flex items-center gap-2 truncate"><User className="w-3.5 h-3.5 text-gray-400" /> {b.cliente || "—"}</p>
        <p className="flex items-center gap-2 truncate"><Building2 className="w-3.5 h-3.5 text-gray-400" /> {b.obra || "—"}</p>
        {b.deadline && (
          <p className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-gray-400" /> {formatDate(b.deadline)}</p>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-between">
        <span className="font-bold text-gray-900">{formatPYG(b.total)}</span>
        <StatPill count={b.items_with_frente_count} total={b.items_count} />
      </div>

      {formatted && b.formatted_by_name && (
        <p className="text-[11px] text-gray-400 mt-2">Por {b.formatted_by_name} · {b.formatted_at ? formatDate(b.formatted_at) : ""}</p>
      )}

      <div className="mt-4 flex items-center gap-2">
        {!formatted ? (
          <button
            onClick={() => onOpen(b._id, false)}
            data-testid={`formatear-btn-${b._id}`}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors"
          >
            <Edit3 className="w-4 h-4" /> Formatear
          </button>
        ) : (
          <>
            <button
              onClick={() => onOpen(b._id, true)}
              data-testid={`ver-formato-btn-${b._id}`}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 font-semibold text-sm hover:bg-gray-200 transition-colors"
            >
              <Eye className="w-4 h-4" /> Ver Formato
            </button>
            <button
              onClick={() => onReopen(b._id)}
              disabled={reopening}
              data-testid={`reabrir-btn-${b._id}`}
              className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl border border-amber-200 text-amber-700 font-semibold text-sm hover:bg-amber-50 transition-colors disabled:opacity-50"
            >
              {reopening ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Reabrir
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function FormateoPresupuestos() {
  const { user } = useAuth();
  const [tab, setTab] = useState("sin_formato");
  const [budgets, setBudgets] = useState([]);
  const [frentes, setFrentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reopeningId, setReopeningId] = useState(null);
  const [openBudget, setOpenBudget] = useState(null); // {id, readOnly}

  useEffect(() => { loadFrentes(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadBudgets(); /* eslint-disable-next-line */ }, [tab]);

  const loadFrentes = async () => {
    try {
      const { data } = await axios.get(`${API}/listas/frentes`);
      setFrentes(data || []);
    } catch { /* noop */ }
  };

  const loadBudgets = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/purchases/formateo`, { params: { status: tab } });
      setBudgets(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los presupuestos");
    } finally {
      setLoading(false);
    }
  };

  const handleReopen = async (id) => {
    setReopeningId(id);
    try {
      await axios.post(`${API}/purchases/formateo/${id}/reabrir`);
      toast.success("Presupuesto reabierto para edición");
      loadBudgets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reabrir");
    } finally {
      setReopeningId(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="formateo-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="p-2 rounded-xl bg-pink-100">
          <Layers className="w-6 h-6 text-pink-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Formateo de Presupuestos Aprobados</h1>
          <p className="text-sm text-gray-500">Asigná un frente de obra a cada ítem de los presupuestos aprobados.</p>
        </div>
        <button onClick={loadBudgets} className="ml-auto p-2 rounded-lg hover:bg-gray-100 text-gray-500" data-testid="refresh-btn">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mt-5 mb-5 border-b border-gray-200">
        {[
          { id: "sin_formato", label: "Sin Formato", icon: ClipboardList },
          { id: "con_formato", label: "Con Formato", icon: CheckCircle2 },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
              tab === t.id ? "border-pink-600 text-pink-600" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}
        </div>
      ) : budgets.length === 0 ? (
        <div className="text-center py-20 text-gray-400" data-testid="formateo-empty">
          <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">
            {tab === "sin_formato" ? "No hay presupuestos aprobados pendientes de formateo." : "Todavía no formateaste ningún presupuesto."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {budgets.map((b) => (
            <BudgetCard
              key={b._id}
              b={b}
              formatted={tab === "con_formato"}
              onOpen={(id, ro) => setOpenBudget({ id, readOnly: ro })}
              onReopen={handleReopen}
              reopening={reopeningId === b._id}
            />
          ))}
        </div>
      )}

      {openBudget && (
        <FormateoTablaModal
          budgetId={openBudget.id}
          frentes={frentes}
          readOnly={openBudget.readOnly}
          onClose={() => setOpenBudget(null)}
          onClosed={loadBudgets}
        />
      )}
    </div>
  );
}
